import { useState } from "react";
import DOMPurify from "dompurify";
import { CircleAlert, ExternalLink, RotateCw } from "lucide-react";
import { PeerAvatar } from "@/components/common/PeerAvatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { INTERVIEWER } from "@/lib/roles";
import { cn } from "@/lib/utils";

// Links in the platform's HTML open in a new tab: following one in this tab
// would unload the page and drop the participant out of the call.
DOMPurify.addHook("afterSanitizeAttributes", node => {
  if (node.tagName === "A" && node.getAttribute("href")) {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

/** Rich text from the platform's editor, sanitized before it is rendered. */
const RichText = ({ html, testId, ...props }) => (
  <div
    className="rich-text text-sm leading-relaxed text-muted-foreground"
    data-testid={testId}
    {...props}
    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
  />
);

/**
 * The platform stores experience dates at midnight UTC, so they are read in
 * UTC; in the viewer's time zone January 1st is still December 31st west of
 * Greenwich (inventory 15.37).
 */
function parseUtcDate(date) {
  if (!date) {
    return null;
  }
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

/** "4 yrs 3 mos": how long a role lasted, or has lasted so far. */
export function durationOf(startDate, endDate, now = new Date()) {
  const start = parseUtcDate(startDate);
  const end = parseUtcDate(endDate) || now;
  if (!start || end < start) {
    return null;
  }
  const months =
    (end.getUTCFullYear() - start.getUTCFullYear()) * 12 +
    (end.getUTCMonth() - start.getUTCMonth());
  if (months < 1) {
    return null;
  }
  const years = Math.floor(months / 12);
  const rest = months % 12;
  return [years ? plural(years, "yr") : null, rest ? plural(rest, "mo") : null]
    .filter(Boolean)
    .join(" ");
}

const SectionTitle = ({ children }) => (
  <h3 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
    {children}
  </h3>
);

const Chips = ({ items }) => (
  <div className="flex flex-wrap gap-1">
    {items.map((item, index) => (
      <Badge
        key={`${item}-${index}`}
        variant="secondary"
        className="rounded-md font-normal"
      >
        {item}
      </Badge>
    ))}
  </div>
);

/** Label and value rows, Cal.com's What / When / Who. */
const Facts = ({ rows }) => {
  const shown = rows.filter(row => row.value);
  if (!shown.length) {
    return null;
  }
  return (
    <dl className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-x-3 gap-y-3 text-sm">
      {shown.map(row => (
        <div key={row.label} className="contents">
          <dt className="pt-0.5 text-muted-foreground">{row.label}</dt>
          <dd className="min-w-0">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
};

const names = list => (list || []).map(item => item.name).filter(Boolean);

const splitList = text =>
  (text || "")
    .split(",")
    .map(item => item.trim())
    .filter(Boolean);

const Experience = ({ experiences }) => (
  <ol className="relative flex flex-col gap-5 border-l pl-4" data-mask>
    {experiences.map((experience, index) => {
      const start = parseUtcDate(experience.start_date)?.getUTCFullYear();
      const end = parseUtcDate(experience.end_date)?.getUTCFullYear();
      const duration = durationOf(experience.start_date, experience.end_date);
      const technologies = splitList(experience.technologies);
      return (
        <li key={index} className="relative flex flex-col gap-1.5">
          <span className="absolute top-1.5 -left-[21px] size-2.5 rounded-full border-2 border-card bg-muted-foreground/60" />
          <div>
            <p className="text-sm font-medium">{experience.title}</p>
            <p className="text-sm text-muted-foreground">
              {experience.project_name}
            </p>
            <p className="text-xs text-muted-foreground tabular-nums">
              {start ?? "?"} – {end ?? "Today"}
              {duration ? ` · ${duration}` : null}
            </p>
          </div>
          {experience.description ? (
            <RichText html={experience.description} />
          ) : null}
          {technologies.length ? <Chips items={technologies} /> : null}
        </li>
      );
    })}
  </ol>
);

const CandidateProfile = ({ profile }) => {
  const mainTechnologies = names(profile.main_technologies);
  const otherTechnologies = names(profile.other_technologies);
  const languages = names(profile.languages);
  const experiences = profile.experiences || [];
  return (
    <div className="flex flex-col gap-6">
      <Facts
        rows={[
          {
            label: "Main stack",
            value: mainTechnologies.length ? (
              <Chips items={mainTechnologies} />
            ) : null,
          },
          {
            label: "Also works with",
            value: otherTechnologies.length ? (
              <Chips items={otherTechnologies} />
            ) : null,
          },
          {
            label: "Languages",
            value: languages.length ? languages.join(", ") : null,
          },
        ]}
      />
      {profile.description ? (
        <section className="flex flex-col gap-2">
          <SectionTitle>About</SectionTitle>
          <RichText
            html={profile.description}
            testId="profile_description"
            data-mask
          />
        </section>
      ) : null}
      {experiences.length ? (
        <section className="flex flex-col gap-3">
          <SectionTitle>Experience</SectionTitle>
          <Experience experiences={experiences} />
        </section>
      ) : null}
    </div>
  );
};

/**
 * The project's screening questions (or this candidate's, when the project
 * generates them per candidate), for a platform that does not send the
 * candidate's answers yet.
 */
const ScreeningQuestions = ({ questions }) => (
  <div className="flex flex-col gap-3">
    <div className="flex flex-col gap-1">
      <SectionTitle>Screening questions</SectionTitle>
      <p className="text-xs text-muted-foreground">
        What the candidate answered on video before this interview.
      </p>
    </div>
    <ol className="divide-y rounded-lg border text-sm">
      {questions.map((question, index) => (
        <li key={index} className="flex gap-3 p-3">
          <span className="text-muted-foreground tabular-nums">
            {index + 1}
          </span>
          <span>{question.label}</span>
        </li>
      ))}
    </ol>
  </div>
);

// Lengths past which a clamp would hide part of the text in the panel.
const LONG_ANSWER = 180;
const LONG_SUMMARY = 420;

const UNANSWERED_LABELS = {
  answered: "No transcript yet",
  skipped: "Skipped",
  unanswered: "No answer",
};

/**
 * Text cut to a few lines until it is opened. `clamp` is the Tailwind
 * line-clamp class, written out in full so Tailwind generates it.
 */
const ClampedText = ({ text, limit, clamp, className, testId }) => {
  const [open, setOpen] = useState(false);
  const isLong = text.length > limit;
  return (
    <div className="flex flex-col items-start gap-1">
      <p
        className={cn(
          "whitespace-pre-line",
          className,
          isLong && !open && clamp
        )}
        data-testid={`${testId}_text`}
        data-mask
      >
        {text}
      </p>
      {isLong ? (
        <button
          type="button"
          className="text-xs font-medium text-brand hover:underline"
          aria-expanded={open}
          data-testid={`${testId}_toggle`}
          onClick={() => setOpen(current => !current)}
        >
          {open ? "Show less" : "Show more"}
        </button>
      ) : null}
    </div>
  );
};

const ScreeningAnswer = ({ question, number }) => {
  const answer =
    question.status === "answered" ? question.answer?.trim() : null;
  return (
    <li className="flex flex-col gap-1.5 p-3" data-testid="screening_answer">
      <div className="flex items-start gap-3">
        <span className="w-3 shrink-0 text-muted-foreground tabular-nums">
          {number}
        </span>
        <p className="min-w-0 flex-1 font-medium">{question.label}</p>
        {question.technology ? (
          <Badge
            variant="secondary"
            className="max-w-[45%] shrink justify-start rounded-md font-normal"
            title={question.technology}
          >
            <span className="truncate">{question.technology}</span>
          </Badge>
        ) : null}
      </div>
      {answer ? (
        <div className="pl-6">
          <ClampedText
            text={answer}
            limit={LONG_ANSWER}
            clamp="line-clamp-3"
            className="text-muted-foreground"
            testId="screening_answer"
          />
        </div>
      ) : (
        <div className="pl-6">
          <Badge variant="outline" className="rounded-md font-normal">
            {UNANSWERED_LABELS[question.status] || "No answer"}
          </Badge>
        </div>
      )}
    </li>
  );
};

/**
 * The candidate's screening as the platform's report shows it to the client:
 * the AI summary, then each question with what the candidate said. Grades and integrity flags are left out on purpose (the platform
 * does not send them to this unauthenticated endpoint).
 */
const ScreeningDetails = ({ screening }) => {
  const sections = (screening.sections || []).filter(
    section => section.questions?.length
  );
  const highlights = screening.highlights || [];
  const offsets = sections.reduce(
    (starts, section, index) => [
      ...starts,
      index ? starts[index - 1] + sections[index - 1].questions.length : 0,
    ],
    []
  );
  return (
    <div className="flex flex-col gap-6">
      {screening.summary || highlights.length ? (
        <section
          className="flex flex-col gap-2"
          data-testid="screening_summary"
        >
          <SectionTitle>Summary</SectionTitle>
          <div className="flex flex-col gap-2 text-sm" data-mask>
            {screening.summary ? (
              <ClampedText
                text={screening.summary}
                limit={LONG_SUMMARY}
                clamp="line-clamp-6"
                testId="screening_summary"
              />
            ) : null}
            {highlights.length ? (
              <ul className="flex list-disc flex-col gap-1 pl-5 text-muted-foreground">
                {highlights.map((highlight, index) => (
                  <li key={index}>{highlight}</li>
                ))}
              </ul>
            ) : null}
          </div>
        </section>
      ) : null}
      {sections.map((section, index) => (
        <section
          key={section.key || section.title}
          className="flex flex-col gap-2"
          data-testid={`screening_section_${section.key}`}
        >
          <SectionTitle>
            {section.title}
            <span className="ml-1.5 tabular-nums">
              {section.questions.length}
            </span>
          </SectionTitle>
          <ol className="divide-y rounded-lg border text-sm">
            {section.questions.map((question, position) => (
              <ScreeningAnswer
                key={position}
                question={question}
                number={offsets[index] + position + 1}
              />
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
};

const hasScreeningAnswers = screening =>
  Boolean(screening?.sections?.some(section => section.questions?.length));

const CandidateDetails = ({ interviewInfo }) => {
  const { profile, project } = interviewInfo;
  const siteUrl = process.env.REACT_APP_SITE;
  const profileUrl = project
    ? profile.candidate_link
    : `${siteUrl}/public/profile/${profile.uuid}`;
  const headline = [profile.title, profile.team_name]
    .filter(Boolean)
    .join(" at ");
  const questions = project?.questions || [];
  const screening = hasScreeningAnswers(interviewInfo.screening)
    ? interviewInfo.screening
    : null;
  const screeningCount = screening
    ? screening.sections.reduce(
        (sum, section) => sum + (section.questions?.length || 0),
        0
      )
    : questions.length;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <PeerAvatar name={profile.name} className="size-10 text-sm" />
        <div className="min-w-0">
          {profileUrl ? (
            <button
              type="button"
              data-testid="profile_link_btn"
              onClick={() =>
                window.open(profileUrl, "_blank", "noopener,noreferrer")
              }
              className="flex items-center gap-1.5 text-left text-base font-semibold hover:underline"
            >
              {profile.name}
              <ExternalLink className="size-3.5 text-muted-foreground" />
            </button>
          ) : (
            <p className="text-base font-semibold">{profile.name}</p>
          )}
          {headline ? (
            <p className="text-sm text-muted-foreground">{headline}</p>
          ) : null}
        </div>
      </div>
      {screeningCount ? (
        <Tabs defaultValue="profile" className="gap-4">
          <TabsList className="w-full">
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="questions" data-testid="questions_tab">
              Screening
              <span className="text-muted-foreground tabular-nums">
                {screeningCount}
              </span>
            </TabsTrigger>
          </TabsList>
          <TabsContent value="profile">
            <CandidateProfile profile={profile} />
          </TabsContent>
          <TabsContent value="questions">
            {screening ? (
              <ScreeningDetails screening={screening} />
            ) : (
              <ScreeningQuestions questions={questions} />
            )}
          </TabsContent>
        </Tabs>
      ) : (
        <CandidateProfile profile={profile} />
      )}
    </div>
  );
};

const ProjectDetails = ({ project }) => {
  const technologies = names(project.technologies);
  return (
    <div className="flex flex-col gap-5">
      <div>
        <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
          The role
        </p>
        <h2 className="text-base font-semibold">{project.name}</h2>
      </div>
      <Facts
        rows={[
          {
            label: "Required technologies",
            value: technologies.length ? <Chips items={technologies} /> : null,
          },
        ]}
      />
      {project.description ? (
        <section className="flex flex-col gap-2">
          <SectionTitle>About the role</SectionTitle>
          <RichText html={project.description} testId="project_description" />
        </section>
      ) : null}
    </div>
  );
};

const LoadingDetails = () => (
  <div className="flex flex-col gap-3" aria-label="Loading interview details">
    {[60, 40, 90, 75, 85].map(width => (
      <div
        key={width}
        className="h-4 animate-pulse rounded bg-muted"
        style={{ width: `${width}%` }}
      />
    ))}
  </div>
);

const ErrorDetails = ({ onRetry }) => (
  <div
    className="flex flex-col items-start gap-3"
    data-testid="interview_info_error"
  >
    <div className="flex items-center gap-2 text-sm font-medium">
      <CircleAlert className="size-4 text-destructive" />
      We couldn&apos;t load the interview details
    </div>
    <p className="text-sm text-muted-foreground">
      The call works without them. Try again in a moment.
    </p>
    <Button
      variant="outline"
      size="sm"
      onClick={onRetry}
      data-testid="interview_info_retry"
    >
      <RotateCw />
      Try again
    </Button>
  </div>
);

/**
 * Who the interviewer is talking to (the candidate and their screening),
 * or, for the candidate's side, the role they are interviewing for.
 * `info` is the useInterviewInfo() result.
 */
export const InterviewInfoDisplay = ({ info, role }) => {
  const isInterviewer = role === INTERVIEWER;
  const project = info.data?.project;

  let content = null;
  if (info.status === "loading") {
    content = <LoadingDetails />;
  } else if (info.status === "error") {
    content = <ErrorDetails onRetry={info.retry} />;
  } else if (isInterviewer && info.data?.profile) {
    content = <CandidateDetails interviewInfo={info.data} />;
  } else if (!isInterviewer && project) {
    content = <ProjectDetails project={project} />;
  }

  if (!content) {
    return null;
  }
  return (
    <ScrollArea className="h-full" data-testid="interview_info">
      <div className="p-5 [overflow-wrap:anywhere]">{content}</div>
    </ScrollArea>
  );
};

/**
 * Whether this role gets the panel at all: the interviewer always (it also
 * shows loading and errors), the candidate's side only when there is a
 * project to show.
 */
export function isPanelAvailable(info, role) {
  if (role === INTERVIEWER) {
    return true;
  }
  return info.status === "ready" && Boolean(info.data?.project);
}
